import { Fragment, useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import PropTypes from 'prop-types';
import DynamicQuantitySelect from 'dibs-dynamic-quantity-select';
import get from 'lodash.get';
import styles from './PdpSharedQuantitySelect.scss';
const INITIAL_QUANTITY = 1;

const getSelectedSku = ({ item, selectedSkuId }) => {
    const skus = get(item, 'skus') || [];
    const skuId = selectedSkuId || item.defaultSkuId;
    return skus.find(sku => sku.serviceId === skuId);
};

const getCtaQuantity = (selectedSku, selectedQuantity) => {
    const { hasUnlimitedQuantity, quantity } = selectedSku || {};
    const isValidCtaQuantity = hasUnlimitedQuantity || Number(selectedQuantity) <= quantity;
    return isValidCtaQuantity ? selectedQuantity : INITIAL_QUANTITY;
};

const getSellerStatusFields = item => {
    const sellerStatus = get(item, 'seller.status');
    const sellerIsActive = sellerStatus === 'active';
    const sellerIsComplimentary = sellerStatus === 'nopay';
    const sellerIsInternal = sellerStatus === 'internal';

    return { sellerIsActive, sellerIsComplimentary, sellerIsInternal };
};

const PdpSharedQuantitySelectComponent = props => {
    const { children, item, selectedSkuId } = props;
    const [selectedQuantity, setSelectedQuantity] = useState(INITIAL_QUANTITY);
    const [quantitySelectKey, setQuantitySelectKey] = useState(selectedSkuId);
    const selectedSku = getSelectedSku(props);
    const { quantity, quantitySelectOptions, setSize } = selectedSku || {};
    const isRingItem = item?.isRingItem;

    // triggered when selectedSkuId changes
    useEffect(() => {
        // reset back to the initial quantity when selectedSkuId changed
        setSelectedQuantity(INITIAL_QUANTITY);
        // re-construct the DynamicQuantitySelect by changing the key
        setQuantitySelectKey(selectedSkuId);
    }, [selectedSkuId]);

    if (quantitySelectOptions) {
        const { isDeleted, isSold, isListingUnlisted } = item;
        const ctaQuantity = getCtaQuantity(selectedSku, selectedQuantity);
        const { sellerIsActive, sellerIsComplimentary, sellerIsInternal } = getSellerStatusFields(
            item
        );
        const showQuantitySelect =
            (sellerIsActive || sellerIsInternal || sellerIsComplimentary) &&
            !isDeleted &&
            !isSold &&
            !isRingItem &&
            !isListingUnlisted;

        return (
            <Fragment>
                <div
                    className={`boxSizingWrapper ${styles.quantitySelect}`}
                    data-tn="quantity-select-wrapper"
                >
                    {showQuantitySelect && (
                        <DynamicQuantitySelect
                            key={quantitySelectKey}
                            availableQuantity={quantity}
                            hasAnimatedPlaceholder
                            onChange={value => setSelectedQuantity(value)}
                            value={selectedQuantity}
                            options={quantitySelectOptions}
                            soldAsSet={setSize && setSize > 1}
                        />
                    )}
                </div>
                {children(ctaQuantity)}
            </Fragment>
        );
    } else {
        return children(null);
    }
};

PdpSharedQuantitySelectComponent.propTypes = {
    item: PropTypes.object.isRequired,
    children: PropTypes.func.isRequired,
    selectedSkuId: PropTypes.string,
};

export const PdpSharedQuantitySelect = createFragmentContainer(PdpSharedQuantitySelectComponent, {
    item: graphql`
        fragment PdpSharedQuantitySelect_item on Item {
            defaultSkuId
            isSold
            isDeleted
            isRingItem
            isListingUnlisted
            seller {
                status
            }
            skus {
                serviceId
                quantity
                hasUnlimitedQuantity
                quantitySelectOptions {
                    label
                    value
                }
                setSize
            }
        }
    `,
});
